import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';

import type { Resource } from '@setvi/shared/interfaces';

import { useSubscribedMutation } from 'Hooks/React-Query';

import {
  useLocations,
  generateDynamicLocationMutation
} from '../../../api/locations';
import { usePrintOrderCart } from '../../../hooks/use-print-order-cart';
import type { PrintOrderItemCustomization } from '../../../types';

interface UseLocationDialogArgs {
  resource: Resource;
  initial?: PrintOrderItemCustomization;
  isEdit: boolean;
  onClose: () => void;
}

/**
 * State and submit for the location dialog. The rep enters a store's location
 * id, generates its PDF, and only then can add the asset — so the generated
 * preview must always match what is in the input.
 */
export const useLocationDialog = ({
  resource,
  initial,
  isEdit,
  onClose
}: UseLocationDialogArgs) => {
  const { enqueueSnackbar } = useSnackbar();
  const { addItem, updateItem, isSyncing } = usePrintOrderCart();

  const [location, setLocation] = useState(initial?.locationId ?? '');
  // Generated location PDF url (preview) + filename (sent on sync).
  const [previewUrl, setPreviewUrl] = useState(initial?.locationPreviewUrl);
  const [fileName, setFileName] = useState(initial?.locationFileName);
  // The id the preview was GENERATED for. Editing the input never clears the
  // preview, it only blocks Add to Cart until the user regenerates.
  const [generatedFor, setGeneratedFor] = useState(
    initial?.locationPreviewUrl ? initial?.locationId : undefined
  );

  const { mutateAsync: generate, isLoading: generating } =
    useSubscribedMutation(generateDynamicLocationMutation());

  // The resource's own location id prefills the input, once, and only while the
  // rep has not typed anything.
  const { locationData, loading: locationLoading } = useLocations(
    resource.ID,
    true
  );
  const prefilledRef = useRef(false);

  useEffect(() => {
    if (prefilledRef.current) return;
    const locationId = locationData?.LocationId;
    if (!locationId) return;

    if (!location.trim()) setLocation(locationId);
    prefilledRef.current = true;
  }, [locationData, location]);

  const handleGenerate = async () => {
    const locationId = location.trim();
    if (!locationId) return;

    try {
      const data = await generate({ resourceId: resource.ID, locationId });
      setPreviewUrl(data?.DownloadURLWithSas ?? undefined);
      setFileName(data?.FileName ?? undefined);
      setGeneratedFor(locationId);
    } catch {
      // Error toast handled globally by the mutation cache.
    }
  };

  const handleConfirm = async () => {
    const customization = {
      addLocation: true,
      locationId: location.trim(),
      locationFileName: fileName,
      locationPreviewUrl: previewUrl
    };

    try {
      if (isEdit) {
        await updateItem(resource.ID, customization);
        enqueueSnackbar('Changes saved.', { variant: 'success' });
      } else {
        await addItem(resource, customization);
        enqueueSnackbar('Added to cart.', { variant: 'success' });
      }
      onClose();
    } catch {
      // Keep the dialog open on failure; the mutation cache toasts the error.
    }
  };

  const busy = locationLoading || generating;

  return {
    input: {
      value: location,
      onChange: setLocation,
      disabled: busy,
      generating: busy,
      canGenerate: !busy && !!location.trim(),
      onGenerate: handleGenerate
    },
    preview: {
      // The generated PDF, falling back to the asset's own until there is one.
      document: {
        ...resource,
        DownloadURLWithSas: previewUrl ?? resource.DownloadURLWithSas
      },
      pages: [{ label: 'Location', pdfUrl: previewUrl }],
      hasPreview: !!previewUrl,
      generating
    },
    footer: {
      isSyncing,
      // Add to Cart needs a preview generated for the current input.
      canConfirm: !!previewUrl && location.trim() === generatedFor,
      handleConfirm
    }
  };
};
